// Chat presentation — how the ONE conversation shows itself, pure vocabulary.
//
// The design brief's §5–6: the author has a continuous relationship with the
// Assistant. There is ONE persistent message system; what reads as "different
// chats" are FILTERED PROJECTIONS over the same message graph. The Assistant now
// has ONE home — its own conversation TAB (the retired corner float, with its
// minimized handle and compact box, was removed). What remains here are the two
// axes that still vary independently of that single home:
//
//   • ChatScope — WHICH projection of the message graph is shown. The active
//     scope must be visibly named ("Showing: this material") with a one-step
//     switch to the wider scope (resource → project → workspace).
//   • ChatAnchor — what the resource scope FOLLOWS: the active tab (default;
//     context moves when the author switches resource) or one explicitly
//     pinned resource.
//
// Workspace boundaries are HARD permission boundaries — a projection never
// mixes content the author may not see; that is the backend's contract, these
// types only carry which projection was asked for.
//
// Discipline (dev-ts): named variants on a `type` discriminant, summed; no
// meaning-swapping boolean ("pinned: boolean" would be the smell — pinned
// carries a resource, following does not, so they are different shapes).

import { exhaustSwitchCase } from "@bilda/utils";
import type { ResourceRef } from "../Navigation/types.js";
import type { ChatEntry, DraftingPhase } from "./types.js";

// === Scope — which projection of the message graph ============================
// From narrowest to widest. `resource` shows messages associated with one
// resource (plus its relevant project context); `project` shows everything
// project-related; `workspace` the workspace-wide discussions. (An `everything`
// arm existed but was never offered nor reachable as a current scope — the host
// serves only resource / workspace and derives project as the resourceless
// fallback — so it was dropped as dead weight, subtractive audit 2026-08-19.)
export type ResourceScope = {
    readonly type: "resource";
    readonly resource: ResourceRef;
};
export type ProjectScope = { readonly type: "project" };
export type WorkspaceScope = { readonly type: "workspace" };
export type ChatScope = ResourceScope | ProjectScope | WorkspaceScope;
export type ChatScopeKind = ChatScope["type"];

// === Anchor — what the resource scope follows =================================
// `following` (default): the chat's resource context is the ACTIVE tab and
// moves with it; the conversation itself continues within the project.
// `pinned`: the author fastened the chat to one resource; switching tabs no
// longer moves the context until they unpin. Distinct intents (pin/unpin are
// separate commands on the owning surface), distinct shapes here.
export type FollowingAnchor = { readonly type: "following" };
export type PinnedAnchor = {
    readonly type: "pinned";
    readonly resource: ResourceRef;
};
export type ChatAnchor = FollowingAnchor | PinnedAnchor;

// === Density — HOW densely the transcript renders =============================
// A rendering lever (like Text's `size`), orthogonal to presentation/scope/
// anchor: `full` is the roomy reading contract (every turn a full bubble/card);
// `compact` is the quick-ask contract (the latest answer clamped + legible, the
// rest compressed to a breadcrumb, proposals to a one-line chip, author turns to
// a dim line). NOT a meaning-swapping boolean — two named reading contracts a
// renderer picks between; the same conversation state feeds either.
export type ChatDensity = "compact" | "full";

// === Compact transcript split — pure ==========================================
// The compact reading contract keeps ONLY the current exchange legible and
// collapses the rest to a "N earlier" breadcrumb. The current exchange begins at
// the LAST author turn (the ask in front of the author right now); everything
// before it is `earlier`. With no author turn yet (an agent-opened thread) there
// is nothing to collapse — the whole short tail stays `recent`. Total over any
// entry list; the owner still passes the FULL projection, so expanding the
// breadcrumb never refetches. Kept pure + here so both the renderer and its
// unit test read one definition of "what is recent".
export type CompactTranscriptSplit = {
    readonly earlier: readonly ChatEntry[];
    readonly recent: readonly ChatEntry[];
};
export const splitTranscriptForCompact = (
    entries: readonly ChatEntry[],
): CompactTranscriptSplit => {
    let start = 0;
    for (let i = entries.length - 1; i >= 0; i--) {
        if (entries[i]?.type === "author") {
            start = i;
            break;
        }
    }
    return { earlier: entries.slice(0, start), recent: entries.slice(start) };
};

// === Compact rest — pure ======================================================
// The owner's brief: the compact float should rest as "basically just an input
// box" (i princip bara en input-ruta). At REST the compact surface collapses its
// inline transcript, empty-state hero AND suggestion chips away, leaving the
// quiet context head + the composer — the history stays reachable through the
// box's expand controls (to panel / full), not shown at rest. The surface leaves
// rest ONLY while an exchange is LIVE — a turn in flight or its just-resolved
// status — so the author still sees "Drafting…" / the fresh reply as it happens,
// then the box returns to the input-box resting state. Presence of a drafting
// phase is the single liveness signal; everything else (idle history, an empty
// thread) is rest. Kept pure + here beside `splitTranscriptForCompact` so the
// renderer and its unit test read one definition of "when compact rests".
//
// NOT density-aware itself: the caller applies it only under `density ===
// "compact"`, so FULL density is untouched — this predicate answers only the
// liveness question, the caller composes it with the density gate.
export const isCompactAtRest = (drafting: DraftingPhase | undefined): boolean =>
    drafting === undefined;

// === Dangling-colon guard (A-04 Facet C / Dm8, narrow) =========================
// The primary fix for "Nu skapar jag ett quiz…:" lives in the prompt
// (`CHAT_VOICE`, authoringDoctrine.ts) — this is the rendering-side backstop
// for when the model slips anyway. NARROW by design: it trims a trailing bare
// colon off an agent message ONLY when that message is IMMEDIATELY followed by
// the tool-activity trail — the exact shape of "Let me fetch the sources:"
// promising a result that then renders as a card/trail, never as text after
// the colon. A colon anywhere else (mid-sentence, a genuine list lead-in
// followed by more agent prose) is untouched, so O-7's "promote reasoning
// prose" decision is not disturbed for real reasoning. Pure, total; the
// message's own words are otherwise unchanged (no colon ⇒ no-op).
export const trimDanglingColonPreambles = (
    entries: readonly ChatEntry[],
): readonly ChatEntry[] =>
    entries.map((entry, i) => {
        if (entry.type !== "agent") return entry;
        if (entries[i + 1]?.type !== "agent_activity") return entry;
        const trimmed = entry.markdown.trimEnd();
        if (!trimmed.endsWith(":")) return entry;
        return { ...entry, markdown: trimmed.slice(0, -1).trimEnd() };
    });

// === Assistant turn heads — pure ==============================================
// The panel names the speaker once per turn ("Assistant" + the AI marker), not
// per entry: a head goes before the FIRST assistant-side entry after each
// author entry (or at the start of an agent-opened thread). Interruption rows
// (`system`) are neither side, so they neither open nor close a turn. Returns
// the heads' render keys (`type:id`, the transcript's own key scheme).
type EntrySide = "author" | "assistant" | "neither";
const sideOf = (entry: ChatEntry): EntrySide => {
    switch (entry.type) {
        case "author":
        case "information_submission":
            return "author";
        case "system":
        case "unknown":
            return "neither";
        case "agent":
        case "proposal":
        case "plan":
        case "material":
        case "material_building":
        case "export":
        case "information_request":
        case "information_request_pending":
        case "action_offer":
        case "agent_activity":
        case "waiting_change":
            return "assistant";
        default:
            return exhaustSwitchCase(entry);
    }
};
export const entryKey = (entry: ChatEntry): string =>
    `${entry.type}:${entry.id}`;
export const assistantTurnHeads = (
    entries: readonly ChatEntry[],
): ReadonlySet<string> =>
    new Set(
        entries.reduce<{ readonly open: boolean; readonly heads: string[] }>(
            (acc, entry) => {
                const side = sideOf(entry);
                if (side === "author") return { ...acc, open: true };
                if (side === "assistant" && acc.open)
                    return {
                        open: false,
                        heads: [...acc.heads, entryKey(entry)],
                    };
                return acc;
            },
            { open: true, heads: [] },
        ).heads,
    );
